import { CatalogueService } from 'src/app/angularJS/services/catalogue/catalogue.service';
import { ACTToolbarService } from 'src/app/angularJS/services/toolbar.service';
import { UserMessagesService } from 'src/app/angularJS/services/user-messages.service';
import { PermissionHelperService } from 'src/app/angularJS/services/permissionHelpers.service';
import { BreadcrumbService } from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';

/**
* A Controller which is used across all sub states of products
*/
export class ProductPageController {
  public static $inject = [
    '$state',
    '$rootScope',
    '$stateParams',
    'catalogueService',
    'catalogue',
    'ACTToolbarService',
    'UserMessages',
    'catalogueConstants',
    'permissionHelpers',
    '$translate',
    'PrimaryAction',
    'breadcrumbService'
  ];
  public catalogueStatuses: Array<string>;
  public permissionsForCatalogue: InterACT.Interfaces.ICataloguePermissions;
  public availableStatuses: Array<string>;

  constructor(
    private $state: ng.ui.IStateService,
    private $rootScope,
    private $stateParams,
    private catalogueService: CatalogueService,
    public catalogue: InterACT.Interfaces.Catalogues.Catalogue,
    private ActToolbarService: ACTToolbarService,
    private UserMessages: UserMessagesService,
    private catalogueConstants: InterACT.Interfaces.ICatalogueConstants,
    private permissionHelpers: PermissionHelperService,
    private $translate: ng.translate.ITranslateService,
    private primaryAction,
    private breadcrumbService: BreadcrumbService
  ) {
    this.activate();
  }

  /**
   * A method which is used to get a list of available statuses
   *
   * @returns a list of available statuses
   */
  public getAvailableStatuses = () => {
    let availableStatuses;
    let status;
    status = this.catalogue.status.toUpperCase();
    availableStatuses = [];
    if (status === 'DRAFT') {
      availableStatuses = [
        {
          statusLabel: 'STATUSES.AWAITING_AUTHORISATION',
          statusValue: 'AWAITING_AUTHORISATION'
        }
      ];
    } else if (status === 'AWAITING_AUTHORISATION') {
      availableStatuses = [
        {
          statusLabel: 'STATUSES.DRAFT',
          statusValue: 'DRAFT'
        }, {
          statusLabel: 'STATUSES.AUTHORISED',
          statusValue: 'AUTHORISED'
        }
      ];
    }
    return this.availableStatuses = availableStatuses;
  }

  /**
   * A method which is used to get a list of available statuses
   *
   * @param status The status we wish to set on the catalogue
   */
  public updateCatalogueStatus = (status: string) => {
    if (this.catalogueHasProductValidationErrors()) {
      this.UserMessages.showToast('TOAST_MESSAGE.CATALOGUE.STATUS_CHANGED_FAILED');
    } else {
      return this.UserMessages.buildConfirmDialog(
        'CONFIRM_DIALOG.CATALOGUE.CHANGE_STATUS',
        'CONFIRM_DIALOG.CATALOGUE.CHANGE_STATUS_TO',
        { catalogue: this.catalogue.scheme.abbreviation, status: this.$translate.instant('STATUSES.' + status) },
        'CONFIRM_DIALOG.CATALOGUE.CHANGE_STATUS_CONFIRM'
      ).then(() => {
        this.$rootScope.showSpinner = true;
        if (status === 'AWAITING_AUTHORISATION') {
          this.catalogueService.requestAuthorisation(this.catalogue.id, <any>{
            cancellation: false
          }).then(() => {
            this.updateStatus(status);
          });
        } else {
          this.catalogueService.authorisationApproval(this.catalogue.id, <any>{
            cancellation: status === 'REJECTED' ? true : false
          }).then(() => {
            this.updateStatus(status);
          });
        }
      });
    }
  }

  /**
   * A method which is used to find permissions for a catalogue
   */
  public findPermissionsForCatalogue = () => {
    this.permissionsForCatalogue = this.permissionHelpers.getPermissionsForCatalogue(this.catalogue);
  }

  /**
   * A method which is used to check if a catalogue has product validation errors
   *
   * @returns a string with a notification type
   */
  public catalogueHasProductValidationErrors = () => {
    return _.some((<any>this.catalogue).notifications, (notification: InterACT.Interfaces.INotification) => {
      return notification.type === 'PRODUCT_VALIDATION_ERRORS';
    });
  }

  /**
   * A method which is used to take the user to the copy catalogue screen
   */
  public showCopyCatalogue = () => {
    this.$state.go('catalogues.copy', {
      sourceCatalogue: this.catalogue,
      catalogueId: this.catalogue.id
    });
  }

  /**
   * A method which is used to take the user to the edit catalogue screen
   */
  public showEditCatalogue = () => {
    this.$state.go('catalogues.edit', {
      sourceCatalogue: this.catalogue,
      catalogueId: this.catalogue.id
    });
  }

  /**
   * A method which is used to delete a selected catalogue
   */
  public deleteCatalogue = () => {
    return this.UserMessages.buildConfirmDialog(
      'CONFIRM_DIALOG.CATALOGUE.DELETE_THIS',
      'CONFIRM_DIALOG.CATALOGUE.DELETE',
      { catalogue: this.catalogue.scheme.abbreviation },
      'CONFIRM_DIALOG.CATALOGUE.DELETE_CONFIRM'
    ).then(() => {
      this.catalogueService.remove(this.catalogue.id).then(() => {
        this.UserMessages.showToast('TOAST_MESSAGE.CATALOGUE.DELETED');
        this.$state.go('catalogues.list');
      });
    });
  }

  /**
   * A method which is used to create a draft catalogue
   */
  public createDraftCatalogue = () => {
    return this.UserMessages.buildConfirmDialog(
      'CONFIRM_DIALOG.CATALOGUE.CREATE_DRAFT',
      'CONFIRM_DIALOG.CATALOGUE.DRAFT',
      { catalogue: this.catalogue.scheme.abbreviation },
      'CONFIRM_DIALOG.CATALOGUE.CREATE_DRAFT_CONFIRM'
    ).then(() => {
      this.catalogueService.edit(this.catalogue.id, <any>{
        status: 'DRAFT'
      }).then(() => {
        this.UserMessages.showToast('TOAST_MESSAGE.CATALOGUE.DRAFT_CREATED');
        this.$stateParams.catalogueVersion++;
        this.$state.go(this.$state.current.name, this.$stateParams);
      });
    });
  }

  /**
   * A method which is used to show the progress of a status on a catalogue
   *
   * @param currentStatus The current status of a product
   */
  public statusesProgress = (currentStatus: string) => {
    let activeStatusPosition;
    let progress;
    let status;
    let statusPosition;
    let statuses;
    statuses = [];
    activeStatusPosition = this.catalogueConstants.STATUSES.indexOf(currentStatus);

    for (let i = 0; i < this.catalogueConstants.STATUSES.length; i++) {
      status = this.catalogueConstants.STATUSES[i];
      progress = '';
      statusPosition = this.catalogueConstants.STATUSES.indexOf(status);

      if (statusPosition < activeStatusPosition) {
        progress = 'completed';
      } else if (statusPosition === activeStatusPosition) {
        progress = 'active';
      }

      statuses.push({
        status: status,
        progress: progress
      });
    }
    return statuses;
  }

  /**
   * A method which is used to update a status for a catalogue
   *
   * @param status The new status to assign to the catalogue
   */
  private updateStatus(status: string) {
    this.catalogue.status = status;
    this.catalogueStatuses = this.statusesProgress(this.catalogue.status);
    this.UserMessages.showToast('TOAST_MESSAGE.STATUS_CHANGED');
    this.$rootScope.showSpinner = false;
    this.activate();
  }

  /**
   * A method which is used to instantiate the controller
   */
  private activate() {
    this.catalogueStatuses = this.statusesProgress(this.catalogue.status);
    this.getAvailableStatuses();
    this.findPermissionsForCatalogue();

    const actions = [{
      name: 'ACTION_DETAIL.COPY',
      icon: 'content_copy',
      action: () => this.showCopyCatalogue()
    }];

    if (this.permissionsForCatalogue.canCreateDraft) {
      actions.push({
        name: 'ACTION_DETAIL.CREATE_DRAFT',
        icon: 'queue',
        action: () => this.createDraftCatalogue()
      });
    }

    if (this.permissionsForCatalogue.canEdit) {
      actions.push({
        name: 'ACTION_DETAIL.EDIT',
        icon: 'create',
        action: () => this.showEditCatalogue()
      }, {
        name: 'ACTION_DETAIL.DELETE',
        icon: 'delete',
        action: () => this.deleteCatalogue()
      });
    }

    this.primaryAction.setActions(actions);
  }
}
